import { useEffect, useCallback, useState } from "react";
import { api, API_BASE, authedFetch } from "@/lib/api";
import { cn } from "@/lib/util";
import {
  Bell, Plus, Trash2, Send, Loader2, X, CheckCircle2, AlertCircle, Building2, ArrowRight, Clock, Download, Mail
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { RuleFiringsModal } from "@/components/security/RuleFiringsModal";
import { NewRuleModal, TRIGGERS } from "@/components/security/NewRuleModal";
import { usePermissions } from "@/lib/permissions";

export default function AlertRules() {
  const { can } = usePermissions();
  const [rules, setRules] = useState([]);
  const [clients, setClients] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [firing, setFiring] = useState(null);
  const [lastFire, setLastFire] = useState(null);
  const [historyRule, setHistoryRule] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);

  const load = useCallback(async () => {
    const [r, c] = await Promise.all([api.get("/alert-rules"), api.get("/clients")]);
    setRules(r.data); setClients(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta regla?")) return;
    await api.delete(`/alert-rules/${id}`);
    toast.success("Regla eliminada");
    load();
  };

  const toggle = async (r) => {
    await api.patch(`/alert-rules/${r.id}`, { enabled: !r.enabled });
    load();
  };

  const fireTest = async (r) => {
    setFiring(r.id); setLastFire(null);
    try {
      const res = await api.post(`/alert-rules/${r.id}/test`);
      setLastFire({ ruleId: r.id, ...res.data.result });
      if (res.data.result.dispatched > 0) toast.success(`Regla disparada — ${res.data.result.dispatched} correo(s) enviado(s)`);
      else toast.warning(`La regla coincidió pero no se envió (${res.data.result.details?.[0]?.reason || "revisa la configuración"})`);
    } catch (e) {
      toast.error("Prueba fallida");
    } finally { setFiring(null); }
  };

  const sendTestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await api.post("/alert-rules/test-email");
      const r = res.data || {};
      if (r.ok) {
        toast.success(`Correo de prueba enviado a ${r.recipient} · revisa tu bandeja de entrada`);
      } else {
        const first = r.result?.results?.[0] || {};
        let hint = first.reason || r.result?.reason || "revisa la configuración";
        if (r.result?.reason === "smtp_not_configured"
            || r.result?.reason === "email_proxy_not_configured") {
          hint = r.result?.detail
            || "Faltan variables SMTP en el backend (SMTP_HOST, SMTP_USER, SMTP_PASSWORD, SMTP_FROM)";
        } else if (first.reason === "smtp_auth_error") {
          hint = `Autenticación SMTP falló · ${first.detail || "revisa SMTP_USER / SMTP_PASSWORD"}`;
        } else if (first.reason === "sender_refused") {
          hint = `El servidor SMTP rechazó el sender · ${first.detail || "verifica SMTP_FROM"}`;
        } else if (first.reason === "recipient_refused") {
          hint = `El servidor SMTP rechazó al destinatario · ${first.detail || r.recipient}`;
        } else if (first.reason === "smtp_network_error") {
          hint = `No se pudo conectar al SMTP · ${first.detail || "revisa SMTP_HOST/SMTP_PORT"}`;
        } else if (first.detail) {
          try {
            const parsed = JSON.parse(first.detail);
            if (parsed.message) hint = parsed.message;
          } catch { /* keep the generic hint */ }
        }
        toast.warning(`No se envió · ${hint}`, { duration: 8000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error enviando correo de prueba");
    } finally { setTestingEmail(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="alert-rules-page">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Consola · Automatización</div>
          <h1 className="font-display font-bold tracking-tighter" style={{ fontSize: "clamp(36px, 4vw, 56px)" }}>
            Reglas de alerta
          </h1>
          <p className="text-muted-foreground text-sm mt-1 max-w-2xl">
            Dispara alertas por correo cuando ocurran eventos críticos — por cliente o para toda la cartera.
            Usa el SMTP configurado en <Link to="/settings" className="text-primary hover:underline">Configuración</Link>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {can("alert_rules.manage") && (
            <button
              onClick={sendTestEmail}
              disabled={testingEmail}
              className="inline-flex items-center gap-2 border border-border px-3 py-2.5 hover:bg-muted font-mono text-[10px] tracking-widest uppercase disabled:opacity-50"
              data-testid="send-test-email-btn"
              title="Envía un correo de prueba a tu propia dirección para verificar la integración de Resend"
            >
              {testingEmail ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
              Enviar email de prueba
            </button>
          )}
          {can("report.download") && (
            <button
              onClick={async () => {
                try {
                  const r = await authedFetch(`${API_BASE}/alert-rules/firings.csv`);
                  if (!r.ok) throw new Error("HTTP " + r.status);
                  const count = r.headers.get("X-Firings-Count") || "?";
                  const blob = await r.blob();
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url; a.download = `CIBRAXIS-alert-firings-${new Date().toISOString().slice(0,10)}.csv`;
                  document.body.appendChild(a); a.click(); a.remove();
                  URL.revokeObjectURL(url);
                  toast.success(`Exportados ${count} disparos`);
                } catch (e) {
                  toast.error("Falló la exportación: " + e.message);
                }
              }}
              className="inline-flex items-center gap-2 border border-border px-3 py-2.5 hover:bg-muted font-mono text-[10px] tracking-widest uppercase"
              data-testid="export-firings-btn"
            >
              <Download className="w-3 h-3" /> Exportar CSV de disparos
            </button>
          )}
          {can("alert_rules.manage") && (
            <button
              onClick={() => setOpenNew(true)}
              className="inline-flex items-center gap-2 border border-primary bg-primary text-primary-foreground px-4 py-2.5 hover:bg-primary/90 font-medium"
              data-testid="new-rule-btn"
            >
              <Plus className="w-4 h-4" /> Nueva regla
            </button>
          )}
        </div>
      </header>

      {rules.length === 0 ? (
        <div className="border border-dashed border-border bg-card/40 p-16 text-center" data-testid="rules-empty">
          <Bell className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Aún no hay reglas — crea una para recibir alertas por correo en eventos importantes.</p>
        </div>
      ) : (
        <div className="border border-border bg-card/60">
          <table className="w-full text-sm">
            <thead className="bg-background/50">
              <tr className="text-left overline border-b border-border">
                <th className="p-3">Nombre</th>
                <th className="p-3">Disparador</th>
                <th className="p-3">Alcance</th>
                <th className="p-3">Destinatarios</th>
                <th className="p-3">Último disparo</th>
                <th className="p-3">Estado</th>
                <th className="p-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rules.map(r => {
                const t = TRIGGERS.find(x => x.id === r.trigger);
                return (
                  <tr key={r.id} data-row-hover className="border-b border-border/50" data-testid={`rule-row-${r.id}`}>
                    <td className="p-3">
                      <div className="text-sm">{r.name}</div>
                      {t?.hasThreshold && (
                        <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
                          threshold ≥ {r.threshold} pts
                        </div>
                      )}
                    </td>
                    <td className="p-3 font-mono text-[10px] tracking-widest uppercase text-muted-foreground">{t?.label || r.trigger}</td>
                    <td className="p-3 text-xs">
                      {r.client_name ? (
                        <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3 text-primary" />{r.client_name}</span>
                      ) : (
                        <span className="text-muted-foreground">Todos los clientes</span>
                      )}
                    </td>
                    <td className="p-3 font-mono text-[11px] text-muted-foreground max-w-[200px] truncate">
                      {r.recipients?.length ? r.recipients.join(", ") : <span className="italic">predeterminado del tenant</span>}
                    </td>
                    <td className="p-3 font-mono text-[10px] text-muted-foreground">
                      {r.last_fired_at ? new Date(r.last_fired_at).toLocaleString() : "—"}
                    </td>
                    <td className="p-3">
                      <button onClick={() => toggle(r)} data-testid={`toggle-rule-${r.id}`}
                              disabled={!can("alert_rules.manage")}
                              className={"relative w-10 h-5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed " + (r.enabled ? "bg-primary border-primary" : "bg-background border-border")}>
                        <span className={"absolute top-0.5 w-4 h-4 bg-white transition-all " + (r.enabled ? "left-5" : "left-0.5")} />
                      </button>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => setHistoryRule(r)}
                                className="border border-border text-[10px] tracking-widest uppercase px-2 py-1 hover:bg-muted inline-flex items-center gap-1"
                                data-testid={`history-rule-${r.id}`}>
                          <Clock className="w-3 h-3" /> Historial
                        </button>
                        {can("alert_rules.manage") && (
                          <>
                            <button onClick={() => fireTest(r)} disabled={firing === r.id}
                                    className="border border-primary/60 text-primary text-[10px] tracking-widest uppercase px-2 py-1 hover:bg-primary/10 disabled:opacity-50 inline-flex items-center gap-1"
                                    data-testid={`test-rule-${r.id}`}>
                              {firing === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Probar
                            </button>
                            <button onClick={() => remove(r.id)}
                                    className="border border-border p-1.5 hover:border-sev-critical hover:text-sev-critical"
                                    data-testid={`delete-rule-${r.id}`}>
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {lastFire && (
        <div className={"border p-4 text-xs font-mono " +
          (lastFire.dispatched > 0 ? "border-sev-low/50 bg-sev-low/5 text-sev-low" :
            "border-sev-medium/50 bg-sev-medium/5 text-sev-medium")}
             data-testid="last-fire-result">
          <div className="flex items-center gap-2">
            {lastFire.dispatched > 0 ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
            {lastFire.matched} regla(s) coincidieron · {lastFire.dispatched} correo(s) enviado(s)
          </div>
          {lastFire.details?.[0] && (
            <div className="mt-1 text-muted-foreground">
              {lastFire.details[0].reason && `Motivo: ${lastFire.details[0].reason}`}
              {lastFire.details[0].error && ` · ${lastFire.details[0].error}`}
            </div>
          )}
        </div>
      )}

      <div className="border border-border bg-card/40 p-5">
        <div className="overline mb-2">Disparadores disponibles</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {TRIGGERS.map(t => (
            <div key={t.id} className="border border-border p-3">
              <div className="font-mono text-[10px] tracking-widest uppercase text-primary">{t.id}</div>
              <div className="text-sm mt-1">{t.label}</div>
              <div className="text-xs text-muted-foreground mt-1">{t.description}</div>
            </div>
          ))}
        </div>
      </div>

      {openNew && (
        <NewRuleModal
          clients={clients}
          onClose={() => setOpenNew(false)}
          onCreated={() => { setOpenNew(false); load(); toast.success("Regla creada"); }}
        />
      )}
      <RuleFiringsModal rule={historyRule} onClose={() => setHistoryRule(null)} />
    </div>
  );
}
